// Which students this device has written history for that the server has not
// answered for yet (#318).
//
// ── The hole this fills ────────────────────────────────────────────────────
//
// `deleteStudents` asks the server, one `limit(1)` query per collection per
// student (see `STUDENT_REFERENCES` in useStudents.jsx): a `limit()`ed client
// window that hit its bound is indistinguishable from a complete one.
//
// `getDocsFromServer` does not see the SDK's mutation queue or ours, so a
// student whose *only* history is a just-queued attendance or ledger row —
// pending, possibly enqueued offline and durable only in IndexedDB — would
// probe clean and be deleted, and the rows naming them would land afterwards
// pointing at nobody (#318). The server probe covers a studio too big for a
// window; this covers a write too recent to have been answered. Both are
// needed.
//
// ── What this reads, and what it deliberately does not ─────────────────────
//
// The live queue's own records, exactly as `overlay.js` takes them — `pending`
// or `inflight`, never `dead`. For every op landing in one of the collections
// it is handed, it reads the op's own fields for a student id and collects it.
//
// **It reads a mention, not a membership, which is the safe reading.** An
// `arrayRemove` taking a student *off* an event series mentions them; so does a
// queued write that would have removed the last ledger row they had. Both come
// back as "this student has queued history", which retires rather than deletes
// — a status a teacher flips back in the drawer (`planStudentDeletion`).
// Reasoning about what the queue would *leave behind* would mean folding it,
// which is `overlay.js`'s job over a stored window this function does not have.
//
// **A queued op carrying no student field contributes nothing.** Those are
// patches — `{ voided: true }` on an attendance record,
// `{ categoryLabel }` on a series — and a patch is to a document that either
// already exists at the server, where the probe finds it, or was created by
// another live op in this same queue, which does carry the id. A create that is
// neither live nor acknowledged is a dead batch, and its document is not
// coming. So the union of *this* and the server probe covers the collections
// named, and neither half covers them alone.
//
// **It has no opinion about collections.** `STUDENT_REFERENCES` is the list,
// beside the queries it drives; this takes the names as an argument so that a
// collection added there cannot be forgotten here.

/** The two queue states whose ops are still going to happen. */
const LIVE_STATES = new Set(['pending', 'inflight']);

/**
 * The fields a student id can be written into, by the shape it is written in.
 *
 * `studentId` is the scalar the reference collections carry.
 * `studentIds` is `eventSeries`' array — the same field `array-contains` probes
 * — and it arrives either as a plain JSON array or as the elements of an
 * `arrayUnion` / `arrayRemove`, which is why the value is read the same way for
 * every transform: `encodeFields` JSON-stringifies both the value and the
 * sentinel's element list, so one `JSON.parse` serves both.
 */
const SCALAR_FIELD = 'studentId';
const ARRAY_FIELD = 'studentIds';

/**
 * The same two shapes for a **family** id (#346).
 *
 * `familyId` is the scalar the reference collections carry; `familyIds` is
 * `invoiceRequests`' array, which is what `array-contains` probes there. The
 * pair above with a different noun, so the walk is shared.
 */
const FAMILY_SCALAR_FIELD = 'familyId';
const FAMILY_ARRAY_FIELD = 'familyIds';

/** The JSON a queued field carries, or `undefined` if it carries none. */
function parsed(value) {
  if (typeof value !== 'string' || value === '') return undefined;
  try {
    return JSON.parse(value);
  } catch {
    // Unreachable for a row `opCodec.encodeFields` wrote — it stringifies
    // everything — so only another build or a corrupted store produces one.
    // Declining to read it leaves the server's answer standing for that row.
    return undefined;
  }
}

/**
 * Every student id mentioned by a live queued write into one of `collections`.
 *
 * @param records      the live queue's own, as in `writeQueue().records()`.
 * @param uid          the studio, so another studio's leftover batches in the
 *                     same origin's queue cannot answer for this one.
 * @param collections  the collection names that count as history —
 *                     `STUDENT_REFERENCES.map((r) => r.collection)`.
 * @returns a `Set` of student ids. Empty means the queue adds nothing to what
 *          the server said; it never means "no history".
 */
export function queuedStudentMentions(records, uid, collections = []) {
  return queuedMentions(records, uid, collections, SCALAR_FIELD, ARRAY_FIELD);
}

/**
 * The same, for **family** ids (#346).
 *
 * `removeFamily` in useFamilies.jsx refuses on anything it cannot rule out, so
 * a ledger row entered offline against a family keeps that family un-deletable
 * until the server has it.
 *
 * **One blind spot.** `messages` carries its family inside `context`, and
 * `opCodec.encodeFields` stores a map field as one JSON blob under the key
 * `context` — so the walk below, which matches on the field *name*, does not
 * see it. A message already at the server is found by the probe; a message
 * this device has queued and not sent is found by neither.
 *
 * @param collections `FAMILY_REFERENCES.map((r) => r.collection)`.
 */
export function queuedFamilyMentions(records, uid, collections = []) {
  return queuedMentions(records, uid, collections, FAMILY_SCALAR_FIELD, FAMILY_ARRAY_FIELD);
}

/**
 * The shared walk. Private: the two exported names above are the only kinds
 * of reference.
 */
function queuedMentions(records, uid, collections, scalarField, arrayField) {
  const found = new Set();
  const wanted = new Set(collections);
  const prefix = `users/${uid}/`;

  for (const record of records || []) {
    if (!LIVE_STATES.has(record?.state)) continue;
    const fields = record.fields || [];
    for (const op of record.ops || []) {
      if (typeof op?.path !== 'string' || !op.path.startsWith(prefix)) continue;
      // `users/{uid}/{collection}/{docId}` — the collection is the segment
      // after the studio, and a deeper path is not one of these collections.
      const rest = op.path.slice(prefix.length).split('/');
      if (rest.length !== 2 || !wanted.has(rest[0])) continue;

      for (const f of fields) {
        if (f.opId !== op.id) continue;
        if (f.field === scalarField) {
          const value = parsed(f.value);
          if (typeof value === 'string' && value) found.add(value);
        } else if (f.field === arrayField) {
          const value = parsed(f.value);
          if (Array.isArray(value)) {
            for (const id of value) if (typeof id === 'string' && id) found.add(id);
          }
        }
      }
    }
  }
  return found;
}
